/**
 * Доля хвоста публичного рендерера: вид `before_after` (снимки «до» и «после»).
 *
 * WEB-2920, заход 2: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово; резерв места под снимки (`useImageSpaceReserve`) зовёт фасад до `switch` и передаёт результаты готовыми. Метка `case 'before_after'` и хуки остаются в фасаде; ветка вида —
 * функция отрисовки `renderBeforeAfter(ctx)`, замыкания фасада приходят
 * объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует только
 * фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import type React from 'react';
import { blockTextRoles } from '../semanticTextRoles';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/** Ветка `before_after` фасада — тело `case` слово в слово. */
export function renderBeforeAfter(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const {
    block,
    varCtx,
    richText,
    beforeAfterBeforeImage,
    beforeAfterAfterImage,
  } = ctx;
  // WEB-694: подписи «до»/«после» редактируемые (beforeLabel/afterLabel);
  // WEB-785: без них — фолбэк BEFORE/AFTER локализуется по языку воронки
  // (было жёстко по-английски).
  const beforeAfterStrings = getPublicQuizStrings(varCtx?.system?.locale);
  // WEB-1692: подписи «до» и «после» — ОДНА настройка на обе (они
  // однородны, та же группировка по ролям, что у двух колонок). Серая
  // заглушка на месте незагруженной картинки под настройку НЕ попадает:
  // это служебный вид редактора, а не оформление воронки (прямое решение
  // карточки, то же, что по рамкам-заглушкам в WEB-1697). Второй роли у
  // блока поэтому нет вовсе — она названа отсутствующей в реестре.
  const beforeAfterRoles = blockTextRoles(block, 'before_after');
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
      <div style={{ flex: 1 }}>
        <p
          data-testid={`before-after-label-before-${block.id}`}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--color-text-secondary, #8c8c8c)',
            textAlign: 'center',
            marginBottom: 4,
            ...beforeAfterRoles.title,
          }}
        >
          {richText(block.beforeLabel, beforeAfterStrings.beforeLabel)}
        </p>
        {block.beforeImageUrl ? (
          <img
            src={block.beforeImageUrl}
            // WEB-2936: подпись снимка по языку воронки (было зашито по-английски).
            alt={beforeAfterStrings.beforeImagePlaceholder}
            decoding="async"
            // WEB-1994: место под снимок «до» до его загрузки; онError
            // снимает резерв (починка ревизии, useImageSpaceReserve).
            style={beforeAfterBeforeImage.style}
            onError={beforeAfterBeforeImage.onError}
          />
        ) : (
          <div
            data-testid={`before-after-placeholder-before-${block.id}`}
            style={{
              background: '#f5f5f5',
              borderRadius: 'var(--border-radius-md, 8px)',
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bfbfbf',
              fontSize: 13,
            }}
          >
            {beforeAfterStrings.beforeImagePlaceholder}
          </div>
        )}
      </div>
      <div style={{ flex: 1 }}>
        <p
          data-testid={`before-after-label-after-${block.id}`}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--color-text-secondary, #8c8c8c)',
            textAlign: 'center',
            marginBottom: 4,
            ...beforeAfterRoles.title,
          }}
        >
          {richText(block.afterLabel, beforeAfterStrings.afterLabel)}
        </p>
        {block.afterImageUrl ? (
          <img
            src={block.afterImageUrl}
            // WEB-2936: подпись снимка по языку воронки (было зашито по-английски).
            alt={beforeAfterStrings.afterImagePlaceholder}
            decoding="async"
            // WEB-1994: место под снимок «после» до его загрузки; онError
            // снимает резерв (починка ревизии, useImageSpaceReserve).
            style={beforeAfterAfterImage.style}
            onError={beforeAfterAfterImage.onError}
          />
        ) : (
          <div
            data-testid={`before-after-placeholder-after-${block.id}`}
            style={{
              background: '#f5f5f5',
              borderRadius: 'var(--border-radius-md, 8px)',
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bfbfbf',
              fontSize: 13,
            }}
          >
            {beforeAfterStrings.afterImagePlaceholder}
          </div>
        )}
      </div>
    </div>
  );
}
